/**
 * Was im Browserreiter steht — Name und Symbol.
 *
 * ## Warum das nicht in `index.html` steht
 *
 * Dort stand bis zum 01.09.2026 das fruehere Kuerzel des Produkts und
 * „Standardrollenmatrix", und ein
 * Symbol gab es gar nicht: der Reiter trug die Weltkugel des Browsers.
 *
 * Beim Kunden ist beides die falsche Auskunft. Er sieht in seinem Browser den
 * Namen unseres Hauses und den einer Tabelle, aus der der Bestand einmal kam.
 * Was dort hingehört, ist **sein** Name und wofür das Werkzeug da ist.
 *
 * Eine feste Zeile je Instanz in `index.html` wäre der naheliegende Weg und
 * der falsche: der Name steht schon in `PROFILE`, die Farbe auch. Zwei
 * Stellen für dieselbe Angabe laufen früher oder später auseinander — dieses
 * Projekt hat das mit dem HOWOGE-Zuschnitt gerade erst erlebt, der doppelt im
 * Baum stand und acht Tage lang eine andere Aussage traf als die
 * Auslieferung.
 *
 * ## Das Symbol
 *
 * Gezeichnet und nicht als Datei mitgeliefert. Drei Gründe:
 *
 *   - `apps/web/public/` steht in `.gitignore` (die Knopfsonde wird dorthin
 *     kopiert und darf nie mit ausgeliefert werden). Eine Bilddatei hätte
 *     dort keinen Platz.
 *   - Die **Demo als einzelne Datei** duldet keinen externen Verweis. Ein
 *     `data:`-URI ist einer weniger.
 *   - Die Marke gehört dem Mandanten. Gezeichnet trägt das Symbol dessen
 *     Farbe und Anfangsbuchstaben, statt für jede Instanz eine eigene Datei
 *     zu brauchen.
 */

/**
 * Ein quadratisches Symbol mit Anfangsbuchstaben, als `data:`-URI.
 *
 * SVG und nicht PNG: es bleibt bei 16 px so scharf wie bei 180 px, und es
 * kostet ein paar hundert Zeichen statt einer Datei.
 *
 * Der Buchstabe steht als `<text>`, nicht als Pfad. Ein Pfad wäre in jedem
 * Browser gleich, ließe sich aber nur für ein festes Alphabet zeichnen; der
 * Mandantenname ist Konfiguration und kann morgen mit einem anderen
 * Buchstaben beginnen.
 */
export function symbolAlsDatenUri(farbe: string, buchstabe: string): string {
  const svg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">'
    + `<rect width="64" height="64" rx="14" fill="${farbe}"/>`
    + '<text x="32" y="33" fill="#fff" font-family="Segoe UI,Helvetica,Arial,sans-serif"'
    + ' font-size="40" font-weight="700" text-anchor="middle"'
    + ' dominant-baseline="central">'
    + buchstabe
    + '</text></svg>';
  /* Nur das Nötige umschreiben. `#` beendet einen URI sonst als Fragment,
     `<`, `>` und `"` sind in einem Attributwert nicht zulässig. */
  const kodiert = svg
    .replace(/"/g, "'")
    .replace(/#/g, '%23')
    .replace(/</g, '%3C')
    .replace(/>/g, '%3E');
  return `data:image/svg+xml,${kodiert}`;
}

/**
 * Setzt Titel und Symbol des Reiters.
 *
 * Der Titel ist `<Mandant> - IAM` — der Name des Hauses und die Aufgabe,
 * sonst nichts. „Standardrollenmatrix" ist die Herkunft der Daten und kein
 * Name für das Werkzeug; sie stand dort aus der Zeit, als es beides noch
 * nicht auseinanderhielt.
 */
export function reiterSetzen(name: string, farbe: string): void {
  document.title = `${name} - IAM`;

  const buchstabe = (name.trim()[0] ?? '?').toUpperCase();
  let verweis = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!verweis) {
    verweis = document.createElement('link');
    verweis.rel = 'icon';
    document.head.appendChild(verweis);
  }
  verweis.type = 'image/svg+xml';
  verweis.href = symbolAlsDatenUri(farbe, buchstabe);
}
